import { useTranslation } from 'react-i18next';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../../elements/ui/card';
import { AnimatedNumber, busyDimClass, RevealCard } from '../../elements/ui/motion';
import { Skeleton } from '../../elements/ui/skeleton';
import { SlaughterForecastDashboard, SlaughterForecastSummary } from '../../models/slaughter-forecast';
import { slaughterForecastColors as c } from './colors';

const num = (value: number) => value.toLocaleString();

/** A true minus sign rather than a hyphen, so "−35" and "+35" line up in a tabular font. */
const signed = (value: number) => (value > 0 ? `+${num(value)}` : value < 0 ? `−${num(-value)}` : '0');

/** Delivered-minus-expected, with its percentage. Nothing at all when there is no expectation to compare with. */
const Difference = ({ summary }: { summary: SlaughterForecastSummary }) => {
  const { t } = useTranslation();

  if (summary.difference === null) {
    return <p className="mt-3 text-xs text-gray-500">{t('f-slaughter-no-expectation')}</p>;
  }

  const short = summary.difference < 0;
  const Icon = short ? TrendingDown : TrendingUp;

  return (
    <p
      className="mt-3 flex items-center gap-1.5 text-sm font-semibold tabular-nums"
      style={{ color: short ? c.shortfall : c.surplus }}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {signed(summary.difference)}
      {summary.differencePercent !== null && (
        <span className="font-medium">{`(${signed(summary.differencePercent)}%)`}</span>
      )}
      <span className="font-normal text-gray-500">{t(short ? 'f-slaughter-short' : 'f-slaughter-over')}</span>
    </p>
  );
};

/** One labelled figure. The expected side is a dash rather than zero when the farm has nothing to expect from. */
const Figure = ({
  label,
  value,
  dotColor,
  revealed,
  delayMs
}: {
  label: string;
  value: number | null;
  dotColor: string;
  revealed: boolean;
  delayMs: number;
}) => (
  <div className="min-w-0">
    <p className="flex items-center gap-1.5 text-xs text-gray-500">
      <span className="h-2 w-2 shrink-0 rounded-[2px]" style={{ backgroundColor: dotColor }} />
      {label}
    </p>
    <p className="mt-1 text-2xl font-semibold tracking-tight text-gray-900 tabular-nums">
      {value === null ? '–' : <AnimatedNumber value={value} active={revealed} delayMs={delayMs} />}
    </p>
  </div>
);

const SummaryCard = ({
  title,
  badge,
  summary,
  delayMs = 0
}: {
  title: string;
  badge?: string;
  summary: SlaughterForecastSummary;
  delayMs?: number;
}) => {
  const { t } = useTranslation();

  return (
    <RevealCard delayMs={delayMs} className="h-full">
      {(revealed) => (
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-medium text-gray-500">{title}</p>
            {badge && (
              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">{badge}</span>
            )}
          </div>

          {/* Expected first: it is the reference the delivered figure is read against. */}
          <div className="mt-3 grid grid-cols-2 gap-4">
            <Figure
              label={t('f-slaughter-expected')}
              value={summary.expected}
              dotColor={c.expected}
              revealed={revealed}
              delayMs={delayMs + 100}
            />
            <Figure
              label={t('f-slaughter-delivered')}
              value={summary.delivered}
              dotColor={c.delivered}
              revealed={revealed}
              delayMs={delayMs + 100}
            />
          </div>

          <Difference summary={summary} />
        </CardContent>
      )}
    </RevealCard>
  );
};

/**
 * The planning half of the widget: what the piglets already registered will deliver in the coming weeks.
 * Each row names its birth week, so "446 due in w40" reads as "the piglets from w16".
 */
const UpcomingCard = ({ dashboard, delayMs = 0 }: { dashboard: SlaughterForecastDashboard; delayMs?: number }) => {
  const { t } = useTranslation();

  const upcoming = dashboard.weeks.filter((week) => week.isForecast);
  const first = upcoming[0];
  const last = upcoming[upcoming.length - 1];

  return (
    <RevealCard delayMs={delayMs} className="h-full">
      {(revealed) => (
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-medium text-gray-500">
              {upcoming.length > 0
                ? t('f-slaughter-upcoming', { weeks: upcoming.length })
                : t('f-slaughter-upcoming-title-none')}
            </p>
            {first && (
              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
                {first === last
                  ? t('f-slaughter-week-short', { week: first.weekNumber })
                  : t('f-slaughter-week-range', { from: first.weekNumber, to: last.weekNumber })}
              </span>
            )}
          </div>

          {/* A grid like the other two cards' figure row, not a plain div: with no preflight the label keeps
            the browser's paragraph margin, which a grid item keeps and a block collapses - so a block here
            sat 12px higher than its neighbours. */}
          <div className="mt-3 grid">
            <Figure
              label={t('f-slaughter-due')}
              value={dashboard.upcomingExpected}
              dotColor={c.forecastStripe}
              revealed={revealed}
              delayMs={delayMs + 100}
            />
          </div>

          {upcoming.length === 0 ? (
            <p className="mt-3 text-xs text-gray-500">{t('f-slaughter-upcoming-none')}</p>
          ) : (
            <div className="mt-3 grid gap-1">
              {upcoming.map((week) => (
                <div
                  key={`${week.year}-${week.weekNumber}`}
                  className="flex items-center justify-between gap-4 text-xs"
                >
                  <span className="text-gray-500">
                    {t('f-slaughter-upcoming-row', { week: week.weekNumber, birthWeek: week.birthWeekNumber })}
                  </span>
                  <span className="font-semibold tabular-nums text-gray-900">
                    {week.expected === null ? '–' : num(week.expected)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </RevealCard>
  );
};

const SummaryCardSkeleton = () => (
  <Card className="h-full">
    <CardContent className="p-5">
      <Skeleton className="h-4 w-32" />
      <div className="mt-3 grid grid-cols-2 gap-4">
        <div>
          <Skeleton className="h-4 w-16" />
          <Skeleton className="mt-1 h-8 w-20" />
        </div>
        <div>
          <Skeleton className="h-4 w-16" />
          <Skeleton className="mt-1 h-8 w-20" />
        </div>
      </div>
      <Skeleton className="mt-3 h-5 w-36" />
    </CardContent>
  </Card>
);

export interface SlaughterSummaryCardsProps {
  dashboard?: SlaughterForecastDashboard | null;
  isBusy: boolean;
}

export default function SlaughterSummaryCards({ dashboard, isBusy }: SlaughterSummaryCardsProps) {
  const { t } = useTranslation();

  // Skeletons only until the first answer: after that a period change dims the figures it is about to
  // replace, so the cards stay mounted and count across to the new values instead of from zero.
  if (!dashboard) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <SummaryCardSkeleton />
        <SummaryCardSkeleton />
        <SummaryCardSkeleton />
      </div>
    );
  }

  const pastWeeks = dashboard.weeks.filter((week) => !week.isForecast);
  const currentWeek = pastWeeks[pastWeeks.length - 1];

  return (
    <div className={`grid grid-cols-1 gap-4 md:grid-cols-3 ${busyDimClass(isBusy)}`}>
      <SummaryCard
        title={t('f-slaughter-this-week')}
        badge={currentWeek && t('f-slaughter-week-short', { week: currentWeek.weekNumber })}
        summary={dashboard.currentWeek}
      />
      <SummaryCard
        title={t('f-slaughter-period', { weeks: dashboard.weeksCount })}
        summary={dashboard.period}
        delayMs={70}
      />
      <UpcomingCard dashboard={dashboard} delayMs={140} />
    </div>
  );
}
